import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Receipt, Loader2, Download, AlertTriangle, CalendarClock, CreditCard, ReceiptText, MessageSquare } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin/platform-billing`;
const SESSION_KEY = 'platform_invoice_session';
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const chf = (n) => 'CHF ' + (Number(n) || 0).toLocaleString('de-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const d = (s) => (s ? new Date(s).toLocaleDateString('de-CH') : '—');

const STATUS = {
  open: { label: 'Offen', cls: 'bg-amber-100 text-amber-700' },
  paid: { label: 'Bezahlt', cls: 'bg-emerald-100 text-emerald-700' },
  overdue: { label: 'Überfällig', cls: 'bg-red-100 text-red-700' },
  void: { label: 'Storniert', cls: 'bg-slate-100 text-slate-500' },
};
const SUB_STATUS = {
  active: 'Aktiv', trialing: 'Testphase', canceling: 'In Kündigung',
  cancelled: 'Beendet', none: 'Kein Abo',
};

export default function PlatformInvoicesPage() {
  const [data, setData] = useState(null);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const [r, u] = await Promise.all([
        axios.get(API, auth()),
        axios.get(`${API}/whatsapp-usage`, auth()).catch(() => null),
      ]);
      setData(r.data);
      if (u) setUsage(u.data);
    } catch { toast.error('Rechnungen konnten nicht geladen werden'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // Rückkehr vom Checkout: Zahlstatus prüfen & Rechnung ggf. als bezahlt markieren
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const status = params.get('status');
    const sid = localStorage.getItem(SESSION_KEY);
    if (status === 'success' && sid) {
      (async () => {
        try {
          const r = await axios.get(`${API}/invoices/pay/status/${sid}`, auth());
          if (r.data?.paid) toast.success('Zahlung bestätigt – Rechnung ist beglichen');
          else toast.message('Zahlung noch nicht bestätigt. Bitte in Kürze erneut prüfen.');
        } catch { /* ignore */ }
        localStorage.removeItem(SESSION_KEY);
        window.history.replaceState({}, '', '/meine-rechnungen');
        load();
      })();
    } else if (status === 'cancel') {
      toast.message('Zahlung abgebrochen');
      localStorage.removeItem(SESSION_KEY);
      window.history.replaceState({}, '', '/meine-rechnungen');
    }
  }, []);

  const payInvoice = async (inv) => {
    setBusy(inv.id);
    try {
      const r = await axios.post(`${API}/invoices/${inv.id}/pay`, { origin_url: window.location.origin }, auth());
      if (r.data?.checkout_url) {
        localStorage.setItem(SESSION_KEY, r.data.session_id);
        window.location.href = r.data.checkout_url;
      } else {
        toast.error('Checkout konnte nicht gestartet werden');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Zahlung konnte nicht gestartet werden');
    } finally { setBusy(''); }
  };

  const downloadPdf = async (inv) => {
    setBusy(inv.id);
    try {
      const r = await axios.get(`${API}/invoices/${inv.id}/pdf`, { ...auth(), responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url; a.download = `${inv.number}.pdf`; document.body.appendChild(a); a.click();
      a.remove(); window.URL.revokeObjectURL(url);
    } catch { toast.error('PDF konnte nicht geladen werden'); }
    finally { setBusy(''); }
  };

  const downloadReceipt = async (inv) => {
    setBusy(inv.id);
    try {
      const r = await axios.get(`${API}/invoices/${inv.id}/receipt`, { ...auth(), responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url; a.download = `Zahlungsbeleg_${inv.number}.pdf`; document.body.appendChild(a); a.click();
      a.remove(); window.URL.revokeObjectURL(url);
    } catch { toast.error('Zahlungsbeleg konnte nicht geladen werden'); }
    finally { setBusy(''); }
  };

  if (loading || !data) {
    return <div className="flex items-center justify-center py-20 text-slate-400" data-testid="platform-invoices-loading"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  }

  const { invoices = [], state = {}, open_sum = 0 } = data;

  return (
    <div className="mx-auto max-w-5xl space-y-6 p-4 sm:p-6" data-testid="platform-invoices-page">
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-bold text-slate-900">
          <Receipt className="h-6 w-6" /> Meine Rechnungen
        </h1>
        <p className="mt-1 text-sm text-slate-500">Deine Plattform-Rechnungen von InvictaX — Zahlstatus und PDF-Download.</p>
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="pi-kpi-open">
          <div className="text-xs font-medium text-slate-500">Offener Betrag</div>
          <div className="mt-1 text-2xl font-bold text-slate-900">{chf(open_sum)}</div>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="pi-kpi-status">
          <div className="text-xs font-medium text-slate-500">Abo-Status</div>
          <div className="mt-1 flex items-center gap-2 text-lg font-semibold text-slate-900"><CalendarClock className="h-4 w-4 text-slate-400" /> {SUB_STATUS[state.status] || state.status || '—'}</div>
          {state.status === 'canceling' && <div className="mt-1 text-xs text-amber-600">Kündigung wirksam zum {d(state.cancel_effective_at)}</div>}
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="pi-kpi-count">
          <div className="text-xs font-medium text-slate-500">Rechnungen</div>
          <div className="mt-1 text-2xl font-bold text-slate-900">{invoices.length}</div>
        </div>
      </div>

      {state.billing_suspended && (
        <div className="flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" data-testid="pi-suspended">
          <AlertTriangle className="h-5 w-5" /> Dein Zugang ist wegen offener Plattform-Rechnungen eingeschränkt. Bitte begleiche den offenen Betrag.
        </div>
      )}

      {usage && (usage.conversations > 0 || usage.will_be_invoiced) && (
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50/50 p-5 shadow-sm" data-testid="wa-usage-card">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-2 text-sm font-semibold text-emerald-900">
                <MessageSquare className="h-4 w-4" /> WhatsApp-Kosten diesen Monat
              </div>
              <p className="mt-0.5 text-xs text-emerald-700/80">
                Laufender Monat ({d(usage.period_start)} – {d(usage.period_end)}) · {usage.conversations} Konversationen
                {usage.will_be_invoiced
                  ? ' · wird mit der nächsten Monatsrechnung verrechnet'
                  : ' · aktuell nicht weiterverrechnet'}
              </p>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-emerald-900" data-testid="wa-usage-total">{chf(usage.amount)}</div>
              {usage.markup_percent > 0 && <div className="text-[11px] text-emerald-700/70">inkl. {Math.round(usage.markup_percent)}% Aufschlag</div>}
            </div>
          </div>
          {usage.lines.length > 0 && (
            <div className="mt-3 overflow-x-auto">
              <table className="w-full text-xs" data-testid="wa-usage-table">
                <thead><tr className="text-left text-[11px] text-emerald-700/70">
                  <th className="py-1">Kategorie</th><th className="py-1 text-right">Anzahl</th>
                  <th className="py-1 text-right">Satz</th><th className="py-1 text-right">Betrag</th>
                </tr></thead>
                <tbody>
                  {usage.lines.map((ln) => (
                    <tr key={ln.category} className="border-t border-emerald-100">
                      <td className="py-1 text-slate-700">{ln.label}</td>
                      <td className="py-1 text-right text-slate-600">{ln.count}</td>
                      <td className="py-1 text-right text-slate-500">{chf(ln.rate)}</td>
                      <td className="py-1 text-right text-slate-800">{chf(ln.cost)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        {invoices.length === 0 ? (
          <p className="py-8 text-center text-sm text-slate-400" data-testid="pi-empty">Noch keine Rechnungen vorhanden.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="pi-table">
              <thead><tr className="border-b border-slate-200 text-left text-xs text-slate-500">
                <th className="px-3 py-2">Nr.</th><th className="px-3 py-2">Periode</th>
                <th className="px-3 py-2">Rechnungsdatum</th><th className="px-3 py-2">Fällig</th>
                <th className="px-3 py-2 text-right">Total</th><th className="px-3 py-2">Status</th><th className="px-3 py-2"></th>
              </tr></thead>
              <tbody>
                {invoices.map((i) => {
                  const st = STATUS[i.status] || { label: i.status, cls: 'bg-slate-100 text-slate-600' };
                  return (
                    <tr key={i.id} className="border-b border-slate-100" data-testid={`pi-row-${i.number}`}>
                      <td className="px-3 py-2 font-mono text-xs text-slate-700">{i.number}</td>
                      <td className="px-3 py-2 text-slate-500">{i.period_key} {i.interval === 'year' ? '(Jahr)' : ''}</td>
                      <td className="px-3 py-2 text-slate-500">{d(i.issued_at || i.created_at)}</td>
                      <td className="px-3 py-2 text-slate-500">{d(i.due_at)}</td>
                      <td className="px-3 py-2 text-right font-semibold text-slate-900">{chf(i.total)}</td>
                      <td className="px-3 py-2"><span className={`rounded px-2 py-0.5 text-xs font-medium ${st.cls}`}>{st.label}{i.dunning_level > 0 ? ` · M${i.dunning_level}` : ''}</span></td>
                      <td className="px-3 py-2 text-right">
                        <div className="inline-flex items-center gap-2">
                          {(i.status === 'open' || i.status === 'overdue') && (
                            <button data-testid={`pi-pay-${i.number}`} onClick={() => payInvoice(i)} disabled={busy === i.id} title="Jetzt bezahlen" className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-50">
                            {busy === i.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />} Bezahlen
                          </button>
                          )}
                          {i.status === 'paid' && (
                            <button data-testid={`pi-receipt-${i.number}`} onClick={() => downloadReceipt(i)} disabled={busy === i.id} title="Zahlungsbeleg herunterladen" className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-200 bg-emerald-50 px-2.5 py-1.5 text-xs font-medium text-emerald-700 hover:bg-emerald-100 disabled:opacity-50">
                            {busy === i.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <ReceiptText className="h-4 w-4" />} Beleg
                          </button>
                          )}
                          <button data-testid={`pi-pdf-${i.number}`} onClick={() => downloadPdf(i)} disabled={busy === i.id} title="PDF herunterladen" className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50">
                            {busy === i.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} PDF
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
